import { useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";

const CONSENT_KEY = "ogstay-cookie-consent";

const COPY = {
  it: {
    title: "Cookie Policy | OGStay",
    desc: "Come OGStay usa cookie e strumenti simili: solo tecnici per il funzionamento del sito, analytics e marketing solo con il tuo consenso.",
    updated: "Ultimo aggiornamento: settembre 2026",
    sections: [
      {
        h: "Cosa sono i cookie",
        body: [
          "I cookie sono piccoli file di testo che i siti salvano sul tuo dispositivo. Su questo sito usiamo anche tecnologie equivalenti come il localStorage del browser.",
        ],
      },
      {
        h: "Cookie necessari (sempre attivi)",
        body: [
          "Servono a far funzionare il sito e non richiedono consenso: preferenza della lingua, consenso cookie, sessione dell'area riservata admin.",
          "Questi strumenti non tracciano la tua navigazione a fini statistici o pubblicitari.",
        ],
      },
      {
        h: "Cookie analytics",
        body: [
          "Ci aiutano a capire come viene usato il sito (pagine visitate, provenienza delle visite) in forma aggregata.",
          "Vengono attivati solo dopo il tuo consenso esplicito dal banner o da questa pagina. Al momento non è attivo alcuno strumento di analytics.",
        ],
      },
      {
        h: "Cookie di marketing",
        body: [
          "Servirebbero a mostrarti contenuti promozionali pertinenti su altri siti.",
          "Al momento OGStay non utilizza cookie di marketing. Se verranno introdotti, resteranno disattivati finché non darai il consenso.",
        ],
      },
      {
        h: "Servizi esterni",
        body: [
          "Portale prenotazioni Kross Booking (ogstay.kross.travel) e mappe OpenStreetMap: sono servizi di terze parti con proprie cookie policy, attivi solo quando li visiti o li usi.",
          "Il pulsante WhatsApp apre un servizio esterno (WhatsApp/Meta) con proprie policy.",
        ],
      },
      {
        h: "Come modificare il tuo consenso",
        body: [
          "Puoi rivedere o revocare il consenso in qualsiasi momento con il pulsante qui sotto: il banner ricomparirà alla prossima visita.",
          "Puoi anche cancellare i cookie dal tuo browser: la procedura dipende dal browser (Chrome, Safari, Firefox, Edge).",
        ],
      },
    ],
    reset: "Modifica il mio consenso cookie",
    resetDone: "Preferenza cancellata: il banner ricomparirà alla prossima visita.",
    privacyLink: "Leggi la Privacy Policy",
  },
  en: {
    title: "Cookie Policy | OGStay",
    desc: "How OGStay uses cookies and similar tools: technical only to run the site, analytics and marketing only with your consent.",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "What cookies are",
        body: [
          "Cookies are small text files that websites store on your device. This site also uses equivalent technologies such as browser localStorage.",
        ],
      },
      {
        h: "Necessary cookies (always on)",
        body: [
          "They make the site work and do not require consent: language preference, cookie consent, admin area session.",
          "These tools do not track your browsing for statistical or advertising purposes.",
        ],
      },
      {
        h: "Analytics cookies",
        body: [
          "They help us understand how the site is used (pages visited, traffic sources) in aggregate form.",
          "They are activated only after your explicit consent from the banner or this page. No analytics tool is currently active.",
        ],
      },
      {
        h: "Marketing cookies",
        body: [
          "They would be used to show you relevant promotional content on other sites.",
          "OGStay currently uses no marketing cookies. If introduced, they will stay off until you consent.",
        ],
      },
      {
        h: "External services",
        body: [
          "Kross Booking portal (ogstay.kross.travel) and OpenStreetMap maps: third-party services with their own cookie policies, active only when you visit or use them.",
          "The WhatsApp button opens an external service (WhatsApp/Meta) with its own policies.",
        ],
      },
      {
        h: "How to change your consent",
        body: [
          "You can review or withdraw consent anytime with the button below: the banner will reappear on your next visit.",
          "You can also delete cookies from your browser: the procedure depends on the browser (Chrome, Safari, Firefox, Edge).",
        ],
      },
    ],
    reset: "Change my cookie consent",
    resetDone: "Preference cleared: the banner will reappear on your next visit.",
    privacyLink: "Read the Privacy Policy",
  },
};

export default function CookiePolicyPage() {
  const { lang } = useLanguage();
  const c = COPY[lang];
  const [reset, setReset] = useState(false);
  useSEO(c.title, c.desc);

  const resetConsent = () => {
    localStorage.removeItem(CONSENT_KEY);
    setReset(true);
    toast.success(c.resetDone);
  };

  return (
    <div data-testid="cookie-policy-page" className="bg-warm px-5 pb-24 pt-36 sm:px-8 sm:pt-44">
      <div className="mx-auto max-w-3xl">
        <h1 data-testid="cookie-policy-title" className="font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
          Cookie Policy
        </h1>
        <p className="mt-3 text-xs font-bold uppercase tracking-[0.15em] text-mute">{c.updated}</p>

        <div className="mt-12 space-y-10">
          {c.sections.map((s, i) => (
            <section key={s.h} data-testid={`cookie-section-${i}`}>
              <h2 className="font-display text-2xl font-semibold text-navy">{s.h}</h2>
              <div className="mt-4 space-y-3">
                {s.body.map((p, j) => (
                  <p key={j} className="border-l-2 border-sky/40 pl-5 text-sm leading-relaxed text-ink/80">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <button
            data-testid="cookie-reset-button"
            onClick={resetConsent}
            disabled={reset}
            className="btn-dark !px-6 !py-3 text-[13px] disabled:opacity-60"
          >
            <RotateCcw className="h-4 w-4" />
            {c.reset}
          </button>
          <Link data-testid="cookie-to-privacy-link" to="/privacy-policy" className="text-sm font-bold text-navy underline decoration-sky decoration-2 underline-offset-4 transition-colors hover:text-med">
            {c.privacyLink}
          </Link>
        </div>
      </div>
    </div>
  );
}
